import { useEffect, useMemo } from 'react';
import type { LanguageValue } from '@elfsight/configurator-contracts';
import type { MessageType } from '@lingui/conf';
import type { I18n } from '@lingui/core';

type Messages = Record<string, MessageType>;
type MessagesRecord = Record<string, string>;

export interface LanguageControlOverridesProps {
  language: LanguageValue | undefined;
  messages: Messages | undefined;
  catalog: Messages;
  i18n: I18n;
}

export function useLanguageControlOverrides({
  language,
  messages,
  catalog,
  i18n
}: LanguageControlOverridesProps) {
  const idMap = useMemo(() => createIdMap(catalog), [catalog]);

  const currentLanguage = language?.language;
  const currentOverrides = currentLanguage
    ? language?.messages[currentLanguage]
    : undefined;

  useEffect(() => {
    if (!currentLanguage || !currentOverrides || !messages) {
      return;
    }

    try {
      const mappedMessages = mapOverrides(currentOverrides, idMap);
      const overriddenMessages = { ...messages, ...mappedMessages };

      i18n.load({ [currentLanguage]: overriddenMessages });
    } catch (error) {
      console.error('Failed to override messages:', error);
    }
  }, [currentLanguage, currentOverrides, messages, idMap]);
}

const createIdMap = (catalog: Messages): MessagesRecord =>
  Object.entries(catalog).reduce<MessagesRecord>((acc, [id, value]) => {
    if (value.message) {
      acc[value.message.toLowerCase()] = id;
    }
    return acc;
  }, {});

const mapOverrides = (
  languageOverrides: MessagesRecord,
  idMap: MessagesRecord
): MessagesRecord =>
  Object.entries(languageOverrides).reduce(
    (acc, [overrideId, overrideValue]) => {
      const matchedId = Object.entries(idMap).find((idValue) => {
        const [sourceId] = idValue;

        return sourceId.toLowerCase() === overrideId.toLowerCase();
      });

      return matchedId ? { ...acc, [matchedId[1]]: overrideValue } : acc;
    },
    {}
  );
